import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import GlobalStyles from './styles/globalStyles';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Attractions from './pages/Attractions';
import AttractionDetails from './pages/AttractionDetails';
import CityDetail from './pages/CityDetail';
import Accommodations from './pages/Accommodations';
import Services from './pages/Services';
import ServicesCatalog from './pages/ServicesCatalog';
import ServiceRequest from './pages/ServiceRequest';
import ShortTermRentals from './pages/services/ShortTermRentals';
import LongTermRentals from './pages/services/LongTermRentals';
import Auctions from './pages/services/Auctions';
import BuySell from './pages/services/BuySell';
import Events from './pages/services/Events';
import Experiences from './pages/services/Experiences';
import NotFound from './pages/NotFound';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import SEOHelmet from './seo/SEOHelmet';
import { LanguageProvider } from './i18n/LanguageContext';
import CookieConsent from './components/ui/CookieConsent';
import { AuthProvider } from './utils/AuthContext';
import UserLogin from './pages/Login';
import AdminLogin from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import AccommodationForm from './pages/admin/AccommodationForm';
import ProtectedRoute from './components/admin/ProtectedRoute';
import PropertyOwner from './pages/services/PropertyOwner';
import PropertyTenant from './pages/services/PropertyTenant';
import Management from './pages/services/Management';
import Furniture from './pages/services/Furniture';
import HomeStaging from './pages/services/HomeStaging';
import Restyling from './pages/services/Restyling';
import Laundry from './pages/services/Laundry';
import LinenRental from './pages/services/LinenRental';
import Cleaning from './pages/services/Cleaning';
import PopUp from './pages/services/PopUp';
import PopUpStore from './pages/services/PopUpStore';
import SmartWorking from './pages/services/SmartWorking';
import Shooting from './pages/services/Shooting';
import Maintenance from './pages/services/Maintenance';
import Affiliation from './pages/services/Affiliation';
import KnowHow from './pages/services/KnowHow';
import Training from './pages/services/Training';
import Courses from './pages/services/Courses';
import PrivateLessons from './pages/services/PrivateLessons';
import TrainingEvents from './pages/services/TrainingEvents';
import Security from './pages/services/Security';
import Insurance from './pages/services/Insurance';
import Card from './pages/services/Card';
import GiftCard from './pages/services/GiftCard';
import FidatyCard from './pages/services/FidatyCard';
import Transportation from './pages/services/Transportation';
import Shuttles from './pages/services/Shuttles';
import Buses from './pages/services/Buses';
import Flights from './pages/services/Flights';
import Trains from './pages/services/Trains';
import Equipment from './pages/services/Equipment';
import Investments from './pages/services/Investments';
import NavigationHandler from './components/layout/NavigationHandler';
import { ModalProvider } from './utils/ModalContext';
import Register from './pages/Register';
import UserDashboard from './pages/Dashboard';
import AdminSettings from './pages/admin/Settings';
import VerifyEmail from './pages/VerifyEmail';
import Network from './pages/services/Network';

// Component for scrolling to top on navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// SEO component for global meta tags
const GlobalSEO = () => {
  return (
    <SEOHelmet
      title="Gestione Immobiliare Premium"
      description="J-O-H offre Gestione Immobiliare Premium in tutta Italia, con espansione internazionale imminente. Prenotazioni a breve/lungo termine, property management, consulenze e servizi completi."
      keywords={[
        "gestione immobiliare premium",
        "property management",
        "alloggi di lusso",
        "affitti brevi",
        "affitti lunghi",
        "ville di lusso",
        "appartamenti di pregio",
        "italia",
        "luxury accommodations",
        "short-term rentals",
        "long-term rentals",
        "real estate services"
      ]}
    />
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
    <LanguageProvider>
      <Router>
        <ModalProvider>
          <GlobalStyles />
          <GlobalSEO />
          <ScrollToTop />
          <NavigationHandler />
          <CookieConsent />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/about" element={<Layout><About /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/attractions" element={<Layout><Attractions /></Layout>} />
            <Route path="/attraction-details" element={<Layout><AttractionDetails /></Layout>} />
            <Route path="/accommodations" element={<Layout><Accommodations /></Layout>} />
            <Route path="/services" element={<Layout><Services /></Layout>} />
            <Route path="/services/catalog" element={<Layout><ServicesCatalog /></Layout>} />
            <Route path="/services/request/:serviceId" element={<Layout><ServiceRequest /></Layout>} />
            <Route path="/services/short-term-rentals" element={<Layout><ShortTermRentals /></Layout>} />
            <Route path="/services/long-term-rentals" element={<Layout><LongTermRentals /></Layout>} />
            <Route path="/services/auctions" element={<Layout><Auctions /></Layout>} />
            <Route path="/services/buy-sell" element={<Layout><BuySell /></Layout>} />
            <Route path="/services/events" element={<Layout><Events /></Layout>} />
            <Route path="/services/experiences" element={<Layout><Experiences /></Layout>} />
            <Route path="/services/property-owner" element={<Layout><PropertyOwner /></Layout>} />
            <Route path="/services/property-tenant" element={<Layout><PropertyTenant /></Layout>} />
            <Route path="/services/management" element={<Layout><Management /></Layout>} />
            <Route path="/services/furniture" element={<Layout><Furniture /></Layout>} />
            <Route path="/services/home-staging" element={<Layout><HomeStaging /></Layout>} />
            <Route path="/services/restyling" element={<Layout><Restyling /></Layout>} />
            <Route path="/services/laundry" element={<Layout><Laundry /></Layout>} />
            <Route path="/services/linen-rental" element={<Layout><LinenRental /></Layout>} />
            <Route path="/services/cleaning" element={<Layout><Cleaning /></Layout>} />
            <Route path="/services/pop-up" element={<Layout><PopUp /></Layout>} />
            <Route path="/services/pop-up-store" element={<Layout><PopUpStore /></Layout>} />
            <Route path="/services/smart-working" element={<Layout><SmartWorking /></Layout>} />
            <Route path="/services/shooting" element={<Layout><Shooting /></Layout>} />
            <Route path="/services/maintenance" element={<Layout><Maintenance /></Layout>} />
            <Route path="/services/affiliation" element={<Layout><Affiliation /></Layout>} />
            <Route path="/services/know-how" element={<Layout><KnowHow /></Layout>} />
            <Route path="/services/training" element={<Layout><Training /></Layout>} />
            <Route path="/services/courses" element={<Layout><Courses /></Layout>} />
            <Route path="/services/private-lessons" element={<Layout><PrivateLessons /></Layout>} />
            <Route path="/services/training-events" element={<Layout><TrainingEvents /></Layout>} />
            <Route path="/services/security" element={<Layout><Security /></Layout>} />
            <Route path="/services/insurance" element={<Layout><Insurance /></Layout>} />
            <Route path="/services/card" element={<Layout><Card /></Layout>} />
            <Route path="/services/gift-card" element={<Layout><GiftCard /></Layout>} />
            <Route path="/services/fidaty-card" element={<Layout><FidatyCard /></Layout>} />
            <Route path="/services/transportation" element={<Layout><Transportation /></Layout>} />
            <Route path="/services/shuttles" element={<Layout><Shuttles /></Layout>} />
            <Route path="/services/buses" element={<Layout><Buses /></Layout>} />
            <Route path="/services/flights" element={<Layout><Flights /></Layout>} />
            <Route path="/services/trains" element={<Layout><Trains /></Layout>} />
            <Route path="/services/equipment" element={<Layout><Equipment /></Layout>} />
            <Route path="/services/investments" element={<Layout><Investments /></Layout>} />
            <Route path="/services/network" element={<Layout><Network /></Layout>} />
            <Route path="/citta/:cityId" element={<Layout><CityDetail /></Layout>} />
            <Route path="/terms-of-service" element={<Layout><TermsOfService /></Layout>} />
            <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
            <Route path="/cookie-policy" element={<Layout><CookiePolicy /></Layout>} />
            
            {/* Authentication routes */}
            <Route path="/login" element={<UserLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            {/* Removed legacy direct-booking routes */}
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/accommodations/new" element={<ProtectedRoute><AccommodationForm /></ProtectedRoute>} />
            <Route path="/admin/accommodations/edit/:id" element={<ProtectedRoute><AccommodationForm /></ProtectedRoute>} />
            
            {/* 404 route */}
            <Route path="*" element={<Layout><NotFound /></Layout>} />
          </Routes>
        </ModalProvider>
      </Router>
    </LanguageProvider>
    </AuthProvider>
  );
};

export default App;
